import type { Dispatch } from "react";
import type { CommonEditorTab, EditorAction, EditorState } from "./reducers";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import type {
  NavbarRegionsTabContentFragment,
  RegionCardFragment,
} from "../client/generated";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { SidebarDivider } from "../shell/SidebarDivider";
import { NavbarTabContent } from "../shell/NavbarTabContent";
import { RegionsToolbox } from "../regions/RegionsToolbox";
import { RegionCard } from "../regions/RegionCard";
import { ScrollArea } from "@acdc2/ui/components/scroll-area";
import { PageEmptyAlert } from "../pages/PageEmptyAlert";
import { PageUnsegmentedAlert } from "../pages/PageUnsegmentedAlert";
import { useUpdateReadingOrderMutation } from "../client/generated";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

type Props = {
  state: EditorState;
  dispatch: Dispatch<EditorAction>;
  fragment: NavbarRegionsTabContentFragment;
};

export function NavbarRegionsTabContent({ state, dispatch, fragment }: Props) {
  const { t } = useTranslation();

  const [updateReadingOrder] = useUpdateReadingOrderMutation();

  const [activeRegion, setActiveRegion] = useState<RegionCardFragment>();

  const items = [...fragment.regions].sort(
    (a, b) => a.readingOrderIndex - b.readingOrderIndex,
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const persistReadingOrder = async (
    newItems: (typeof fragment)["regions"],
  ) => {
    try {
      await Promise.allSettled([
        ...newItems.map((region, index) =>
          updateReadingOrder({
            variables: {
              regionId: region.id,
              readingOrderIndex: index,
            },
            optimisticResponse: {
              updateRegionReadingOrderIndex: {
                userErrors: [],
                region: {
                  ...region,
                  readingOrderIndex: index,
                },
              },
            },
          }),
        ),
      ]);
    } catch (e) {
      console.error("Failed to persist reading orders", e);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveRegion(event.active.data.current as RegionCardFragment);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const activeId = event.active.id;
    const overId = event.over?.id;

    if (!overId) return;
    if (activeId === overId) return;

    const oldIndex = items.findIndex((region) => region.id === activeId);
    const newIndex = items.findIndex((region) => region.id === overId);
    const newItems = arrayMove(items, oldIndex, newIndex);
    persistReadingOrder(newItems);

    setActiveRegion(undefined);
  };

  return (
    <NavbarTabContent
      value={"regions" satisfies CommonEditorTab}
      title={t("NavbarRegionsTabContent.title")}
    >
      <div>
        <div className="p-4">
          <RegionsToolbox state={state} dispatch={dispatch} />
        </div>
        <SidebarDivider />
      </div>
      <ScrollArea className="flex-1">
        <div className="flex flex-col gap-4 p-4">
          {!fragment.available && <PageUnsegmentedAlert />}
          {items.length === 0 && <PageEmptyAlert />}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items}
              strategy={verticalListSortingStrategy}
            >
              {items.map((region) => (
                <RegionCard
                  key={region.id}
                  fragment={region}
                  state={state}
                  dispatch={dispatch}
                />
              ))}
            </SortableContext>

            <DragOverlay>
              {activeRegion ? (
                <RegionCard
                  fragment={activeRegion}
                  state={state}
                  dispatch={dispatch}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </ScrollArea>
    </NavbarTabContent>
  );
}
